import Phaser from 'phaser'
import { CreatureFactory } from '../entities/CreatureFactory'
import { CreatureType } from '../types/GameTypes'

interface TileDefinition {
  type: string
  color: string
  detail: string
  isObject?: boolean
}

interface CharacterColors {
  skin: string
  hair: string
  shirt: string
  pants: string
  outline: string
}

export class BootScene extends Phaser.Scene {
  private progressBar!: Phaser.GameObjects.Graphics

  constructor() {
    super({ key: 'BootScene' })
  }

  preload(): void {
    // Create loading bar
    this.createLoadingBar()

    // Load external assets
    this.load.image('asset-tree', 'assets/objects/tree.png')
    this.load.image('asset-tree-variation', 'assets/objects/tree-variation.png')
    this.load.image('asset-crate', 'assets/objects/crate.png')
    this.load.image('asset-wooden-crate', 'assets/objects/wooden-crate.png')
    this.load.image('asset-torch', 'assets/objects/torch.png')
    this.load.image('asset-barrel', 'assets/objects/multiple-objects.png')
    this.load.image('citizen', 'assets/characters/citizen.png')

    // Load custom tile assets
    this.load.image('tile-cobblestone', 'assets/objects/cobblestone-tile.png')
    this.load.image('tile-grass', 'assets/objects/grass-tile.png')
    this.load.image('asset-well', 'assets/objects/well.png')

    // Generate all assets procedurally
    this.generateTileset()
    this.generatePlayerSprite()
    this.generateCreatureSprites()
    this.generateUIElements()
  }

  private createLoadingBar(): void {
    const width = this.scale.width
    const height = this.scale.height

    // Loading text
    this.add.text(width / 2, height / 2 - 50, 'Loading...', {
      fontSize: '24px',
      color: '#ffffff',
      fontFamily: 'Arial'
    }).setOrigin(0.5)

    // Progress bar background
    const progressBg = this.add.graphics()
    progressBg.fillStyle(0x333333, 1)
    progressBg.fillRect(width / 2 - 150, height / 2, 300, 20)

    // Progress bar fill
    this.progressBar = this.add.graphics()

    // Update progress
    this.load.on('progress', (value: number) => {
      this.progressBar.clear()
      this.progressBar.fillStyle(0x4ade80, 1)
      this.progressBar.fillRect(width / 2 - 148, height / 2 + 2, 296 * value, 16)
    })
  }

  private generateTileset(): void {
    const tileSize = 32
    const tilesPerRow = 8
    const canvas = document.createElement('canvas')
    canvas.width = tileSize * tilesPerRow
    canvas.height = tileSize * tilesPerRow
    const ctx = canvas.getContext('2d')!

    // Tile definitions with modern colors
    const tiles: TileDefinition[] = [
      // Row 0: Grass variations
      { type: 'grass1', color: '#4ade80', detail: '#22c55e' },
      { type: 'grass2', color: '#22c55e', detail: '#16a34a' },
      { type: 'grass3', color: '#86efac', detail: '#4ade80' },
      { type: 'grassFlower', color: '#4ade80', detail: '#f472b6' },
      // Water variations
      { type: 'water1', color: '#38bdf8', detail: '#0ea5e9' },
      { type: 'water2', color: '#0ea5e9', detail: '#0284c7' },
      { type: 'waterEdge', color: '#7dd3fc', detail: '#38bdf8' },
      { type: 'waterDeep', color: '#0369a1', detail: '#075985' },

      // Row 1: Sand and dirt
      { type: 'sand1', color: '#fcd34d', detail: '#fbbf24' },
      { type: 'sand2', color: '#fbbf24', detail: '#f59e0b' },
      { type: 'dirt1', color: '#a3a3a3', detail: '#737373' },
      { type: 'dirt2', color: '#78716c', detail: '#57534e' },
      // Stone/path
      { type: 'stone1', color: '#9ca3af', detail: '#6b7280' },
      { type: 'stone2', color: '#6b7280', detail: '#4b5563' },
      { type: 'stonePath', color: '#d1d5db', detail: '#9ca3af' },
      { type: 'cobble', color: '#a8a29e', detail: '#78716c' },

      // Row 2: Trees and vegetation
      { type: 'tree1', color: '#166534', detail: '#14532d', isObject: true },
      { type: 'tree2', color: '#15803d', detail: '#166534', isObject: true },
      { type: 'bush', color: '#22c55e', detail: '#16a34a', isObject: true },
      { type: 'rock', color: '#71717a', detail: '#52525b', isObject: true },
      // Building elements
      { type: 'wallH', color: '#78716c', detail: '#57534e', isObject: true },
      { type: 'wallV', color: '#78716c', detail: '#57534e', isObject: true },
      { type: 'roof', color: '#b45309', detail: '#92400e' },
      { type: 'floor', color: '#d6d3d1', detail: '#a8a29e' },

      // Row 3: More building/decoration
      { type: 'door', color: '#92400e', detail: '#78350f' },
      { type: 'window', color: '#7dd3fc', detail: '#38bdf8' },
      { type: 'fence', color: '#a16207', detail: '#854d0e', isObject: true },
      { type: 'barrel', color: '#a16207', detail: '#78350f', isObject: true },
      { type: 'crate', color: '#ca8a04', detail: '#a16207', isObject: true },
      { type: 'well', color: '#6b7280', detail: '#4b5563', isObject: true },
      { type: 'signpost', color: '#a16207', detail: '#78350f', isObject: true },
      { type: 'lamp', color: '#fbbf24', detail: '#f59e0b', isObject: true }
    ]

    tiles.forEach((tile, index) => {
      const x = (index % tilesPerRow) * tileSize
      const y = Math.floor(index / tilesPerRow) * tileSize
      this.drawTile(ctx, x, y, tileSize, tile)
    })

    // Add to texture manager
    this.textures.addCanvas('tiles', canvas)
  }

  private drawTile(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, tile: TileDefinition): void {
    // Base fill
    ctx.fillStyle = tile.color
    ctx.fillRect(x, y, size, size)

    // Add subtle noise/texture
    ctx.fillStyle = tile.detail

    if (tile.type.includes('grass')) {
      // Grass blades
      for (let i = 0; i < 8; i++) {
        const bx = x + Math.random() * (size - 4)
        const by = y + Math.random() * (size - 6)
        ctx.fillRect(bx, by, 2, 4 + Math.random() * 4)
      }
      if (tile.type === 'grassFlower') {
        ctx.fillStyle = '#f472b6'
        ctx.beginPath()
        ctx.arc(x + size / 2, y + size / 2, 3, 0, Math.PI * 2)
        ctx.fill()
      }
    } else if (tile.type.includes('water')) {
      // Water ripples
      ctx.strokeStyle = tile.detail
      ctx.lineWidth = 1
      for (let i = 0; i < 3; i++) {
        ctx.beginPath()
        ctx.moveTo(x + 4 + i * 8, y + 8 + i * 6)
        ctx.quadraticCurveTo(x + 8 + i * 8, y + 4 + i * 6, x + 12 + i * 8, y + 8 + i * 6)
        ctx.stroke()
      }
    } else if (tile.type.includes('tree')) {
      // Tree trunk
      ctx.fillStyle = '#78350f'
      ctx.fillRect(x + size / 2 - 3, y + size - 10, 6, 10)
      // Tree foliage (circle)
      ctx.fillStyle = tile.color
      ctx.beginPath()
      ctx.arc(x + size / 2, y + size / 2 - 2, size / 2 - 4, 0, Math.PI * 2)
      ctx.fill()
      // Highlight
      ctx.fillStyle = tile.detail
      ctx.beginPath()
      ctx.arc(x + size / 2 - 4, y + size / 2 - 6, size / 4, 0, Math.PI * 2)
      ctx.fill()
    } else if (tile.type === 'bush') {
      ctx.beginPath()
      ctx.arc(x + size / 2, y + size / 2, size / 2 - 4, 0, Math.PI * 2)
      ctx.fill()
    } else if (tile.type === 'rock') {
      ctx.beginPath()
      ctx.moveTo(x + 4, y + size - 4)
      ctx.lineTo(x + size / 2, y + 4)
      ctx.lineTo(x + size - 4, y + size - 4)
      ctx.closePath()
      ctx.fill()
    } else if (tile.type.includes('stone') || tile.type === 'cobble') {
      // Stone pattern
      for (let i = 0; i < 4; i++) {
        const sx = x + (i % 2) * (size / 2) + 2
        const sy = y + Math.floor(i / 2) * (size / 2) + 2
        ctx.fillStyle = i % 2 === 0 ? tile.color : tile.detail
        ctx.fillRect(sx, sy, size / 2 - 4, size / 2 - 4)
      }
    } else if (tile.type.includes('sand') || tile.type.includes('dirt')) {
      // Scattered dots
      for (let i = 0; i < 12; i++) {
        ctx.fillRect(x + Math.random() * size, y + Math.random() * size, 2, 2)
      }
    } else if (tile.type.includes('wall')) {
      // Brick pattern
      ctx.strokeStyle = tile.detail
      ctx.lineWidth = 1
      for (let row = 0; row < 4; row++) {
        const offset = row % 2 === 0 ? 0 : size / 4
        ctx.strokeRect(x + offset, y + row * 8, size / 2, 8)
      }
    } else if (tile.type === 'barrel') {
      ctx.beginPath()
      ctx.ellipse(x + size / 2, y + size / 2, size / 3, size / 2 - 4, 0, 0, Math.PI * 2)
      ctx.fill()
      ctx.strokeStyle = '#57534e'
      ctx.lineWidth = 2
      ctx.stroke()
    } else if (tile.type === 'crate') {
      ctx.strokeStyle = tile.detail
      ctx.lineWidth = 2
      ctx.strokeRect(x + 4, y + 4, size - 8, size - 8)
      ctx.beginPath()
      ctx.moveTo(x + 4, y + 4)
      ctx.lineTo(x + size - 4, y + size - 4)
      ctx.moveTo(x + size - 4, y + 4)
      ctx.lineTo(x + 4, y + size - 4)
      ctx.stroke()
    } else if (tile.type === 'roof') {
      // Shingle/tile pattern for roof
      ctx.fillStyle = tile.detail
      for (let row = 0; row < 4; row++) {
        const offset = row % 2 === 0 ? 0 : size / 4
        for (let col = 0; col < 3; col++) {
          ctx.beginPath()
          ctx.arc(x + offset + col * (size / 2.5) + 6, y + row * 8 + 4, 5, Math.PI, 0)
          ctx.fill()
        }
      }
      // Dark edge at bottom
      ctx.fillStyle = '#78350f'
      ctx.fillRect(x, y + size - 3, size, 3)
    } else if (tile.type === 'floor') {
      // Wooden plank pattern
      ctx.strokeStyle = tile.detail
      ctx.lineWidth = 1
      for (let i = 0; i < 4; i++) {
        ctx.strokeRect(x + 2, y + i * 8 + 2, size - 4, 6)
      }
    } else if (tile.type === 'door') {
      // Door with frame and handle
      ctx.fillStyle = '#78350f'
      ctx.fillRect(x + 2, y + 2, size - 4, size - 2) // Door frame
      ctx.fillStyle = tile.color
      ctx.fillRect(x + 4, y + 4, size - 8, size - 4) // Door panel
      // Handle
      ctx.fillStyle = '#fbbf24'
      ctx.beginPath()
      ctx.arc(x + size - 8, y + size / 2, 2, 0, Math.PI * 2)
      ctx.fill()
      // Wood grain
      ctx.strokeStyle = tile.detail
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(x + size / 2, y + 6)
      ctx.lineTo(x + size / 2, y + size - 4)
      ctx.stroke()
    } else if (tile.type === 'well') {
      // Stone well with dark center
      ctx.fillStyle = '#1f2937' // Dark water
      ctx.beginPath()
      ctx.arc(x + size / 2, y + size / 2, size / 3, 0, Math.PI * 2)
      ctx.fill()
      // Stone rim
      ctx.strokeStyle = tile.color
      ctx.lineWidth = 4
      ctx.beginPath()
      ctx.arc(x + size / 2, y + size / 2, size / 3 + 2, 0, Math.PI * 2)
      ctx.stroke()
      // Highlight
      ctx.strokeStyle = '#9ca3af'
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.arc(x + size / 2, y + size / 2, size / 3 + 2, Math.PI * 1.2, Math.PI * 1.7)
      ctx.stroke()
    } else if (tile.type === 'lamp') {
      // Lamp post
      ctx.fillStyle = '#57534e' // Post
      ctx.fillRect(x + size / 2 - 2, y + size / 3, 4, size * 2 / 3)
      // Lamp head
      ctx.fillStyle = tile.color
      ctx.beginPath()
      ctx.arc(x + size / 2, y + size / 4, 6, 0, Math.PI * 2)
      ctx.fill()
      // Glow effect
      ctx.fillStyle = 'rgba(251, 191, 36, 0.3)'
      ctx.beginPath()
      ctx.arc(x + size / 2, y + size / 4, 10, 0, Math.PI * 2)
      ctx.fill()
    }

    // Subtle border for definition
    ctx.strokeStyle = 'rgba(0,0,0,0.1)'
    ctx.lineWidth = 1
    ctx.strokeRect(x + 0.5, y + 0.5, size - 1, size - 1)
  }

  private generatePlayerSprite(): void {
    const frameWidth = 32
    const frameHeight = 32
    const framesPerDirection = 4
    const directions = 4 // down, up, left, right

    const canvas = document.createElement('canvas')
    canvas.width = frameWidth * framesPerDirection
    canvas.height = frameHeight * directions
    const ctx = canvas.getContext('2d')!

    // Colors for character
    const colors: CharacterColors = {
      skin: '#fcd5b8',
      hair: '#5c4033',
      shirt: '#3b82f6',
      pants: '#1e3a5f',
      outline: '#333333'
    }

    for (let dir = 0; dir < directions; dir++) {
      for (let frame = 0; frame < framesPerDirection; frame++) {
        const x = frame * frameWidth
        const y = dir * frameHeight
        this.drawCharacterFrame(ctx, x, y, frameWidth, frameHeight, dir, frame, colors)
      }
    }

    // Create an image from the canvas and add as spritesheet
    const img = new Image()
    img.src = canvas.toDataURL()
    img.onload = () => {
      if (!this.textures.exists('player')) {
        this.textures.addSpriteSheet('player', img, {
          frameWidth: frameWidth,
          frameHeight: frameHeight
        })
      }
    }
    // Synchronously add canvas texture as fallback
    this.textures.addCanvas('player-canvas', canvas)
    this.textures.addSpriteSheet('player', canvas as unknown as HTMLImageElement, {
      frameWidth: frameWidth,
      frameHeight: frameHeight
    })
  }

  private drawCharacterFrame(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    direction: number,
    frame: number,
    colors: CharacterColors
  ): void {
    const cx = x + w / 2
    const cy = y + h / 2

    // Walking animation offset
    const legOffset = frame % 2 === 0 ? 2 : -2

    // Body bob
    const bodyBob = Math.abs(Math.sin(frame * Math.PI / 2)) * 1

    ctx.save()

    // Shadow
    ctx.fillStyle = 'rgba(0,0,0,0.2)'
    ctx.beginPath()
    ctx.ellipse(cx, y + h - 4, 8, 4, 0, 0, Math.PI * 2)
    ctx.fill()

    // Legs
    ctx.fillStyle = colors.pants
    if (direction === 0 || direction === 1) { // Front or back
      ctx.fillRect(cx - 5, cy + 4 - bodyBob, 4, 10 + (frame % 2 === 0 ? 1 : 0))
      ctx.fillRect(cx + 1, cy + 4 - bodyBob, 4, 10 + (frame % 2 === 1 ? 1 : 0))
    } else { // Side
      ctx.fillRect(cx - 2 + legOffset, cy + 4 - bodyBob, 4, 10)
    }

    // Body/shirt
    ctx.fillStyle = colors.shirt
    ctx.fillRect(cx - 6, cy - 6 - bodyBob, 12, 12)

    // Arms
    const walkOffset = Math.sin(frame * Math.PI / 2) * 2
    if (direction === 2) { // Left
      ctx.fillStyle = colors.skin
      ctx.fillRect(cx + 4, cy - 2 - bodyBob + walkOffset, 4, 8)
    } else if (direction === 3) { // Right
      ctx.fillStyle = colors.skin
      ctx.fillRect(cx - 8, cy - 2 - bodyBob + walkOffset, 4, 8)
    } else {
      ctx.fillStyle = colors.shirt
      ctx.fillRect(cx - 9, cy - 4 - bodyBob, 4, 8)
      ctx.fillRect(cx + 5, cy - 4 - bodyBob, 4, 8)
      ctx.fillStyle = colors.skin
      ctx.fillRect(cx - 9, cy + 2 - bodyBob, 4, 3)
      ctx.fillRect(cx + 5, cy + 2 - bodyBob, 4, 3)
    }

    // Head
    ctx.fillStyle = colors.skin
    ctx.beginPath()
    ctx.arc(cx, cy - 12 - bodyBob, 7, 0, Math.PI * 2)
    ctx.fill()

    // Hair
    ctx.fillStyle = colors.hair
    if (direction === 0) { // Front
      ctx.beginPath()
      ctx.arc(cx, cy - 14 - bodyBob, 7, Math.PI, Math.PI * 2)
      ctx.fill()
    } else if (direction === 1) { // Back
      ctx.beginPath()
      ctx.arc(cx, cy - 12 - bodyBob, 7, 0, Math.PI * 2)
      ctx.fill()
    } else { // Side
      ctx.beginPath()
      ctx.arc(cx, cy - 14 - bodyBob, 7, Math.PI * 0.8, Math.PI * 1.8)
      ctx.fill()
    }

    // Eyes (front only)
    if (direction === 0) {
      ctx.fillStyle = '#333'
      ctx.fillRect(cx - 3, cy - 13 - bodyBob, 2, 2)
      ctx.fillRect(cx + 1, cy - 13 - bodyBob, 2, 2)
    }

    ctx.restore()
  }

  private generateUIElements(): void {
    // Health bar frame
    const healthCanvas = document.createElement('canvas')
    healthCanvas.width = 200
    healthCanvas.height = 30
    const hctx = healthCanvas.getContext('2d')!

    // Background
    hctx.fillStyle = '#1f2937'
    hctx.beginPath()
    hctx.roundRect(0, 0, 200, 30, 8)
    hctx.fill()

    // Border
    hctx.strokeStyle = '#4b5563'
    hctx.lineWidth = 2
    hctx.beginPath()
    hctx.roundRect(0, 0, 200, 30, 8)
    hctx.stroke()

    this.textures.addCanvas('healthBar', healthCanvas)

    // Direction indicator
    const dirCanvas = document.createElement('canvas')
    dirCanvas.width = 64
    dirCanvas.height = 64
    const dctx = dirCanvas.getContext('2d')!

    dctx.fillStyle = 'rgba(255,255,255,0.1)'
    dctx.beginPath()
    dctx.arc(32, 32, 30, 0, Math.PI * 2)
    dctx.fill()

    this.textures.addCanvas('dirIndicator', dirCanvas)

    // Target indicator (circle around selected target)
    const targetCanvas = document.createElement('canvas')
    targetCanvas.width = 48
    targetCanvas.height = 48
    const tctx = targetCanvas.getContext('2d')!

    tctx.strokeStyle = '#ef4444'
    tctx.lineWidth = 2
    tctx.setLineDash([4, 4])
    tctx.beginPath()
    tctx.arc(24, 24, 20, 0, Math.PI * 2)
    tctx.stroke()

    this.textures.addCanvas('targetIndicator', targetCanvas)
  }

  private generateCreatureSprites(): void {
    const creatureTypes: CreatureType[] = ['rat', 'snake', 'wolf']
    const frameWidth = 32
    const frameHeight = 32
    const framesPerDirection = 4
    const directions = 4 // down, up, left, right

    creatureTypes.forEach(type => {
      const canvas = document.createElement('canvas')
      canvas.width = frameWidth * framesPerDirection
      canvas.height = frameHeight * directions
      const ctx = canvas.getContext('2d')!

      // Generate sprite for each direction and frame
      for (let dir = 0; dir < directions; dir++) {
        for (let frame = 0; frame < framesPerDirection; frame++) {
          const x = frame * frameWidth
          const y = dir * frameHeight

          ctx.save()
          ctx.translate(x, y)

          // Apply direction transforms
          if (dir === 1) {
            // Up - flip vertically (show back)
            ctx.translate(frameWidth / 2, frameHeight / 2)
            ctx.scale(1, -1)
            ctx.translate(-frameWidth / 2, -frameHeight / 2)
          } else if (dir === 2) {
            // Left - flip horizontally
            ctx.translate(frameWidth / 2, 0)
            ctx.scale(-1, 1)
            ctx.translate(-frameWidth / 2, 0)
          }
          // dir 0 = down (default), dir 3 = right (default facing)

          CreatureFactory.generateSprite(ctx, 0, 0, frameWidth, frameHeight, type, frame)
          ctx.restore()
        }
      }

      // Add as spritesheet
      this.textures.addSpriteSheet(`creature-${type}`, canvas as unknown as HTMLImageElement, {
        frameWidth: frameWidth,
        frameHeight: frameHeight
      })
    })
  }

  create(): void {
    // Clean up loading UI before transitioning
    this.progressBar.destroy()

    // Transition to game scene (this stops BootScene)
    this.scene.start('GameScene')
  }
}
